import { TypeDefinitionAttribute } from "../types";

export interface SearchAttributeFilter {
    attributeIndex: string;
    filter: SearchAttributeFilterCase;
}

export interface SearchAttributeFilterCase {
    stringFilter?: SearchStringFilter;
    booleanFilter?: BooleanFilter;
    intFilter?: IntFilter;
    idFilter?: IdFilter;
}

export interface SearchStringFilter {
    equals?: string;
    in?: string[];
    notIn?: string[];
}

export interface BooleanFilter {
    equals?: boolean;
}

export interface IntFilter {
    equals?: number;
    in?: number[];
    notIn?: number[];
}

export interface IdFilter {
    equals?: string;
    in?: string[];
    notIn?: string[];
    exists?: boolean;
}

/**
 * Transforms installed base attribute filters to SearchAttributeFilter format
 * @param filterValues - The filter values object (e.g., from filterParams)
 * @param attributes - The attribute metadata from SearchAssetTdas query
 * @returns Array of SearchAttributeFilter objects
 */
export const transformInstalledBaseAttributeFilters = (
    filterValues: { [key: string]: any },
    attributes: TypeDefinitionAttribute[]
): SearchAttributeFilter[] => {
    // Identify attributes with installedBaseAttributeFilters category or dynamicFilterListKey
    const installedBaseAttributes = attributes.filter(
        attr => attr.categories?.includes("installedBaseAttributeFilters")
             || attr.dynamicFilterListKey === "installedBaseAttributeFilters"
    );

    const filters: SearchAttributeFilter[] = [];

    installedBaseAttributes.forEach(attr => {
        const value = filterValues[attr.index];

        // Skip undefined/null/empty values
        if (value === undefined || value === null || value === '') {
            return;
        }

        // Determine filter case based on attribute type
        const filterCase = buildFilterCase(attr.type, value);

        if (filterCase) {
            filters.push({
                attributeIndex: attr.index,
                filter: filterCase
            });
        }
    });

    return filters;
};

/**
 * Maps attribute type to appropriate filter case
 */
export const buildFilterCase = (
    attributeType: string,
    value: any
): SearchAttributeFilterCase | null => {
    // Handle array values (multi-select)
    if (Array.isArray(value)) {
        if (value.length === 0) return null;

        if (attributeType === "bool" || attributeType === "checkbox") {
            // BooleanFilter only offers `equals` — there is no `in`. A multi-select
            // with both options picked matches everything, so it is not a filter.
            const distinct = Array.from(new Set(value.map(v => v === "true" || v === true)));
            if (distinct.length !== 1) return null;
            return { booleanFilter: { equals: distinct[0] } };
        }
        // Determine type from first element or use string as fallback
        if (attributeType === "number") {
            return { intFilter: { in: value.map(v => parseInt(v)) } };
        }
        // For select with ID values
        if (attributeType === "select") {
            return { idFilter: { in: value } };
        }
        // Default to string filter
        return { stringFilter: { in: value } };
    }

    // Handle single values
    switch (attributeType) {
        case "bool":
        case "checkbox":
            // Values are normally pre-cast by coerceTdaValue; the string check keeps
            // direct callers (and raw URL values) working.
            const boolValue = value === "true" || value === true;
            return { booleanFilter: { equals: boolValue } };

        case "number":
            const numValue = typeof value === 'number' ? value : parseInt(value);
            return { intFilter: { equals: numValue } };

        case "select":
            // Select fields typically use ID values
            return { idFilter: { equals: value } };

        case "text":
        case "multilang":
        case "date":
        default:
            // Default to string filter with equals
            return { stringFilter: { equals: String(value) } };
    }
};
